import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { format } from "date-fns";

import { MileageModel } from "@interfaces/index";
import { QUERY_WIDTHS, formatAmount } from "@utils/index";
import { useCommonStore } from "@stores/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  XSvg,
  Button,
  EditSvg,
  ConfirmModal,
  TrashSvg,
  AssetItem,
} from "@components/index";

export const MileageDetails = ({
  mileage,
  onEdit,
  onRemove,
  onClose,
}: {
  mileage?: MileageModel;
  onEdit: () => void;
  onRemove: () => void;
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const setCommonState = useCommonStore(({ setState }) => setState);

  const [showConfirm, setShowConfirm] = useState(false);

  const isDiBusiness = mileage?.userBusiness.type === "driving_instructor";

  const handleRemove = () => {
    setShowConfirm(false);
    if (onRemove) {
      setCommonState({ isDrawerShown: false });
      onRemove();
    }
  };

  const handleClose = () => {
    setCommonState({ isDrawerShown: false });
    onClose();
  };

  return (
    mileage && (
      <Drawer>
        <DrawerHeader
          onClose={handleClose}
          title={
            <div className="flex flex-col items-center md:items-start">
              <div className="text-300 font-bold text-black-100 md:text-600">
                {format(new Date(mileage.dateFrom), "dd.MM.yy")} -{" "}
                {format(new Date(mileage.dateTo), "dd.MM.yy")}
              </div>
              <div className="text-black-54 md:text-300 md:font-bold">
                {mileage.period.title}
              </div>
            </div>
          }
          rightContent={
            mileage.policies.canDelete ? (
              <button
                type="button"
                className="flex items-center gap-x-2 text-black-54 hover:opacity-80"
                onClick={() => setShowConfirm(true)}
                data-testid="button_removeMileage"
              >
                <TrashSvg />
                {!isMobile && "Delete"}
              </button>
            ) : null
          }
        />
        <DrawerScrollableContainer className="flex h-[calc(100dvh_-_50px)] flex-col justify-between md:h-[calc(100dvh_-_128px)]">
          <div className="pt-4 md:pt-14">
            <div className="pb-4 text-200 font-bold text-black-54 md:text-300">
              {mileage.userBusiness.name}
            </div>
            <div className="w-full rounded-lg bg-secondary-100 px-4 py-4 md:px-6">
              <div className="font-bold text-white-100">
                {isDiBusiness ? "Total" : "Total Miles/Tax Year"}
              </div>
              <div className="pt-6 text-400 font-bold text-white-100">
                {mileage.totalMiles} miles
              </div>
            </div>
            <div className="flex gap-x-1 pt-1">
              <div className="w-1/2 bg-primary-108 px-4 py-4 md:px-6">
                <div className="font-bold text-black-54">Business:</div>
                <div className="pt-6 text-400 font-bold text-black-100">
                  {mileage.businessMiles} miles
                </div>
              </div>
              <div className="w-1/2 bg-primary-108 px-4 py-4 md:px-6">
                <div className="font-bold text-black-54">
                  {isDiBusiness ? "Private:" : "Mileage:"}
                </div>
                <div className="pt-6 text-400 font-bold text-black-100">
                  {isDiBusiness ? (
                    <div className="flex items-end justify-between">
                      <div>{mileage.privateMiles} miles</div>
                      <div className="pb-1 text-200 font-bold text-black-54">
                        {mileage.privateUse}%
                      </div>
                    </div>
                  ) : (
                    `£${formatAmount(mileage.mileageSum)}`
                  )}
                </div>
              </div>
            </div>
            <div className="pb-2 pt-10 text-200 text-black-54 md:text-300">
              Asset
            </div>
            <AssetItem
              type={mileage.asset.type}
              name={mileage.asset.name}
              isDisposed={!!mileage.asset.disposal}
            />
            {mileage.comments && (
              <div className="mt-6 border-t border-t-black-16 pt-5">
                <div className="pb-1 text-200 text-black-54 md:text-300">
                  Comment
                </div>
                <div>{mileage.comments}</div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-y-4 border-t border-t-black-8 pb-2 md:flex-row md:justify-between md:pb-4 md:pt-4">
            <Button
              leftIcon={<EditSvg />}
              title="Edit Mileage"
              height="small"
              dataTestid="button_editMileage"
              onClick={onEdit}
              disabled={!mileage.policies.canUpdate}
            />
            <Button
              variant="secondary"
              leftIcon={<XSvg />}
              title="Cancel"
              height="small"
              onClick={handleClose}
            />
          </div>
        </DrawerScrollableContainer>
        <ConfirmModal
          show={showConfirm}
          confirmButtonTitle="Delete"
          text1="Are you sure?"
          text2="Do you really want to delete this entry?"
          onClickCancel={() => setShowConfirm(false)}
          onClickConfirm={handleRemove}
        />
      </Drawer>
    )
  );
};
